// StackIQ SSE protocol — the pure, testable core of the chat stream handling.
// Keep the constants in sync with express/lib/ai/protocol.js; the paired test
// suites (this file's tests and express/routes/aiRoutes.test.js) assert both
// sides against src/components/ai/sseFixtures.json to catch drift.

// The server emits this status right before composing the final answer; the
// UI clears its interim status display on this exact string.
export const STATUS_COMPILING = 'Compiling response';
export const CHART_FENCE = 'stackiq-chart';

export type AiQuery = { tool: string; input: unknown };

export type AiSseEvent =
  | { type: 'status'; text: string }
  | { type: 'query'; tool: string; input: unknown }
  // Token-streamed answer text. A delta_reset means the streamed text so far
  // was tool-round preamble — discard it. `done` always carries the full
  // canonical content and supersedes any accumulated partial.
  | { type: 'delta'; text: string }
  | { type: 'delta_reset' }
  | { type: 'done'; response: string; truncated?: boolean }
  | { type: 'error'; error: string };

export type AiChatMessage = {
  role: 'user' | 'assistant';
  content: string;
  truncated?: boolean;
  stopped?: boolean;
  error?: boolean;
  queries?: AiQuery[];
  attachments?: { name: string }[];
};

// Incremental frame decoder. Feed each network chunk with the leftover buffer
// from the previous call; complete `data: <json>` lines become events, the
// trailing partial line (possibly mid-JSON) is returned as `rest`.
export const parseSseChunk = (
  buffer: string,
  chunk: string
): { events: AiSseEvent[]; rest: string } => {
  const combined = buffer + chunk;
  const lines = combined.split('\n');
  const rest = lines.pop() ?? '';
  const events: AiSseEvent[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed.startsWith('data:')) continue;
    try {
      events.push(JSON.parse(trimmed.slice(5).trim()));
    } catch {
      // Malformed frame — skip rather than kill the stream.
    }
  }
  return { events, rest };
};

export type AiStreamState = {
  statusText: string | null;
  // Every status in order — the visible "working" trace (consecutive
  // duplicates collapsed).
  trace: string[];
  queries: AiQuery[];
  // Token-streamed answer text accumulated so far.
  partial: string;
  done: boolean;
  response: string | null;
  truncated: boolean;
  error: string | null;
};

export const initialStreamState: AiStreamState = {
  statusText: null,
  trace: [],
  queries: [],
  partial: '',
  done: false,
  response: null,
  truncated: false,
  error: null,
};

// Pure state transition for one event.
export const applyEvent = (state: AiStreamState, event: AiSseEvent): AiStreamState => {
  switch (event.type) {
    case 'status':
      return {
        ...state,
        statusText: event.text === STATUS_COMPILING ? null : event.text,
        trace:
          state.trace[state.trace.length - 1] === event.text
            ? state.trace
            : [...state.trace, event.text],
      };
    case 'query':
      return { ...state, queries: [...state.queries, { tool: event.tool, input: event.input }] };
    case 'delta':
      return { ...state, partial: state.partial + event.text };
    case 'delta_reset':
      return { ...state, partial: '' };
    case 'done':
      return {
        ...state,
        done: true,
        statusText: null,
        partial: '',
        response: event.response,
        truncated: Boolean(event.truncated),
      };
    case 'error':
      return { ...state, done: true, statusText: null, partial: '', error: event.error };
    default:
      return state;
  }
};

// Terminal stream state -> the assistant chat message to append.
export const finalizeMessage = (state: AiStreamState): AiChatMessage => {
  if (state.error) {
    return { role: 'assistant', content: state.error, error: true, queries: state.queries };
  }
  return {
    role: 'assistant',
    content: state.response ?? '',
    truncated: state.truncated,
    queries: state.queries,
  };
};
